import React, { useEffect, useMemo } from "react";
import styled from "styled-components";
import { useProducts } from "../context/ProductContext";
import { Product } from "../types/product";
import ProductCard from "../components/product/ProductCard";
import ProductFilters from "../components/product/ProductFilters";
import Pagination from "../components/ui/Pagination";
import LoadingSpinner from "../components/ui/LoadingSpinner";
import ErrorMessage from "../components/ui/ErrorMessage";
import EmptyState from "../components/ui/EmptyState";

const PRODUCTS_PER_PAGE = 10;

const PageHeader = styled.div`
  display: flex;
  flex-direction: column;
  gap: 1.5rem;
  margin-bottom: 2rem;

  @media (min-width: ${({ theme }) => theme.breakpoints.md}) {
    flex-direction: row;
    align-items: center;
    justify-content: space-between;
  }
`;

const TitleSection = styled.div``;

const PageTitle = styled.h1`
  font-size: 1.75rem;
  font-weight: 800;
  color: ${({ theme }) => theme.colors.text};
  letter-spacing: -0.02em;
`;

const ProductCount = styled.p`
  font-size: 0.9rem;
  color: ${({ theme }) => theme.colors.textSecondary};
  margin-top: 0.25rem;
`;

const Grid = styled.div`
  display: grid;
  grid-template-columns: 1fr;
  gap: 1.5rem;

  @media (min-width: ${({ theme }) => theme.breakpoints.sm}) {
    grid-template-columns: repeat(2, 1fr);
  }

  @media (min-width: ${({ theme }) => theme.breakpoints.lg}) {
    grid-template-columns: repeat(3, 1fr);
  }

  @media (min-width: ${({ theme }) => theme.breakpoints.xl}) {
    grid-template-columns: repeat(4, 1fr);
  }
`;

const ProductListPage: React.FC = () => {
  const {
    state: {
      products,
      categories,
      loading,
      error,
      sortOption,
      filterCategory,
      currentPage,
    },
    loadProducts,
    loadCategories,
    setSortOption,
    setFilterCategory,
    setCurrentPage,
  } = useProducts();

  useEffect(() => {
    if (products.length === 0) {
      loadProducts();
    }
    if (categories.length === 0) {
      loadCategories();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const processedProducts = useMemo(() => {
    let result = [...products];

    // Filter by category
    if (filterCategory) {
      result = result.filter((p) => p.category === filterCategory);
    }

    // Sort
    switch (sortOption) {
      case "price-asc":
        result.sort((a, b) => a.price - b.price);
        break;
      case "price-desc":
        result.sort((a, b) => b.price - a.price);
        break;
      case "category":
        result.sort((a, b) => a.category.localeCompare(b.category));
        break;
    }

    return result;
  }, [products, sortOption, filterCategory]);

  const totalPages = Math.ceil(processedProducts.length / PRODUCTS_PER_PAGE);

  const paginatedProducts = useMemo(() => {
    const start = (currentPage - 1) * PRODUCTS_PER_PAGE;
    return processedProducts.slice(start, start + PRODUCTS_PER_PAGE);
  }, [processedProducts, currentPage]);

  if (loading) {
    return <LoadingSpinner message="Loading products..." />;
  }

  if (error) {
    return <ErrorMessage message={error} onRetry={loadProducts} />;
  }

  return (
    <>
      <PageHeader>
        <TitleSection>
          <PageTitle>Products</PageTitle>
          <ProductCount>
            {processedProducts.length} product
            {processedProducts.length !== 1 ? "s" : ""} found
          </ProductCount>
        </TitleSection>
        <ProductFilters
          sortOption={sortOption}
          filterCategory={filterCategory}
          categories={categories}
          onSortChange={setSortOption}
          onCategoryChange={setFilterCategory}
        />
      </PageHeader>

      {processedProducts.length === 0 ? (
        <EmptyState
          message={
            filterCategory
              ? `No products found in "${filterCategory}" category.`
              : "No products found."
          }
        />
      ) : (
        <>
          <Grid>
            {paginatedProducts.map((product: Product) => (
              <ProductCard key={product.id} product={product} />
            ))}
          </Grid>
          <Pagination
            currentPage={currentPage}
            totalPages={totalPages}
            onPageChange={setCurrentPage}
          />
        </>
      )}
    </>
  );
};

export default ProductListPage;
